import React, { useState, useEffect, useRef } from 'react';
import { Table, Card, Badge, OverlayTrigger, Tooltip, Spinner, Button, Alert } from 'react-bootstrap';
import { authenticatedFetch } from '../helpFunctions/authFetch';
import { useNavigate } from 'react-router-dom';
import { getEnvVariable } from '../helpFunctions/getEnvVariable';

const EloLeaderboard = ({ surveyId, activeVariantIds, autoUpdate = false, hasFiltersChanged, onFiltersApplied }) => {
    const [eloData, setEloData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isStale, setIsStale] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const navigate = useNavigate();
    const apiBaseUrl = getEnvVariable("API_BASE_URL");
    const lastAppliedFilters = useRef(null);
    const updateTimeoutRef = useRef(null);

    useEffect(() => {
        // Initial load
        fetchEloRatings();
        lastAppliedFilters.current = activeVariantIds;
    }, [surveyId]);

    useEffect(() => {
        // Check if filters have changed
        if (lastAppliedFilters.current && activeVariantIds) {
            const filtersChanged = JSON.stringify(lastAppliedFilters.current) !== JSON.stringify(activeVariantIds);
            setIsStale(filtersChanged);

            // Auto-update if enabled and filters changed
            if (autoUpdate && filtersChanged) {
                // Clear any existing timeout
                if (updateTimeoutRef.current) {
                    clearTimeout(updateTimeoutRef.current);
                }

                // Debounce the update to avoid too many API calls
                updateTimeoutRef.current = setTimeout(() => {
                    fetchEloRatings(activeVariantIds);
                }, 1000); // Wait 1 second after last change
            }
        }
    }, [activeVariantIds, autoUpdate]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (updateTimeoutRef.current) {
                clearTimeout(updateTimeoutRef.current);
            }
        };
    }, []);

    const fetchEloRatings = async (variantIds = null) => {
        try {
            setIsLoading(true);
            setIsUpdating(true);
            setError(null);

            // Build URL with variant filter if provided
            let url = `${apiBaseUrl}elo_ratings/${surveyId}`;
            if (variantIds && variantIds.length > 0) {
                const params = new URLSearchParams();
                variantIds.forEach(id => params.append('variant_ids', id));
                url += `?${params.toString()}`;
            }

            const response = await authenticatedFetch(navigate, url);

            if (response.ok) {
                const data = await response.json();
                setEloData(data);
                setIsStale(false);
                lastAppliedFilters.current = variantIds || activeVariantIds;
                if (onFiltersApplied) {
                    onFiltersApplied();
                }
            } else {
                const errorData = await response.json();
                setError(errorData.detail || 'Failed to fetch ELO ratings');
            }
        } catch (err) {
            setError('Failed to fetch ELO ratings');
        } finally {
            setIsLoading(false);
            setIsUpdating(false);
        }
    };

    const handleApplyFilters = () => {
        fetchEloRatings(activeVariantIds);
    };

    const getRankBadge = (rank) => {
        if (rank === 1) return <Badge bg="warning">🥇</Badge>;
        if (rank === 2) return <Badge bg="secondary">🥈</Badge>;
        if (rank === 3) return <Badge bg="danger">🥉</Badge>;
        return <Badge bg="light" text="dark">{rank}</Badge>;
    };



    if (isLoading) {
        return (
            <Card className="mt-4">
                <Card.Header>
                    <h4>🏆 ELO Arena Leaderboard</h4>
                </Card.Header>
                <Card.Body className="text-center">
                    <Spinner animation="border" />
                    <p className="mt-2">Calculating ELO ratings...</p>
                </Card.Body>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="mt-4">
                <Card.Header>
                    <h4>🏆 ELO Arena Leaderboard</h4>
                </Card.Header>
                <Card.Body>
                    <p className="text-danger">{error}</p>
                </Card.Body>
            </Card>
        );
    }

    if (!eloData || !eloData.leaderboard || eloData.leaderboard.length === 0) {
        return (
            <Card className="mt-4">
                <Card.Header>
                    <h4>🏆 ELO Arena Leaderboard</h4>
                </Card.Header>
                <Card.Body>
                    <p>No voting data available yet.</p>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card className="mt-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
                <h4 className={isStale ? "text-muted" : ""}>
                    🏆 ELO Arena Leaderboard
                    {isStale && (
                        <Badge bg="warning" className="ms-2" style={{ fontSize: '0.6em' }}>
                            Filters Changed
                        </Badge>
                    )}
                </h4>
                <div>
                    <Badge bg="info" className="me-2">
                        {eloData.total_battles} Battles
                    </Badge>
                    <Badge bg="success">
                        {eloData.total_votes} Total Votes
                    </Badge>
                </div>
            </Card.Header>
            <Card.Body>
                {isStale && (
                    <Alert variant="warning" className="d-flex justify-content-between align-items-center">
                        <div>
                            <strong>⚠️ Filters have changed</strong>
                            <br />
                            <small>Click "Apply Filters" to recalculate ELO ratings with selected variants</small>
                        </div>
                        <Button
                            variant="warning"
                            onClick={handleApplyFilters}
                            disabled={isUpdating}
                            className="ms-3"
                        >
                            {isUpdating ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Calculating...
                                </>
                            ) : (
                                "Apply Filters to ELO"
                            )}
                        </Button>
                    </Alert>
                )}
                <p className="text-muted small mb-3">
                    Rankings based on ELO rating system (similar to chess ratings).
                    Higher ratings indicate better performance in pairwise comparisons.
                </p>

                <div style={{ opacity: isStale ? 0.6 : 1, transition: 'opacity 0.3s' }}>
                    <Table responsive hover>
                        <thead>
                            <tr>
                                <th>Rank</th>
                                <th>Variant</th>
                                <th>ELO Rating</th>
                                <th>95% Confidence Interval</th>
                                <th>Battles</th>
                            </tr>
                        </thead>
                        <tbody>
                            {eloData.leaderboard.map((item) => (
                                <tr key={item.variant_name}>
                                    <td className="align-middle">
                                        {getRankBadge(item.rank)}
                                    </td>
                                    <td className="align-middle">
                                        <strong>{item.variant_name}</strong>
                                    </td>
                                    <td className="align-middle">
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={
                                                <Tooltip>
                                                    Standard Deviation: ±{item.std_dev}
                                                </Tooltip>
                                            }
                                        >
                                            <Badge bg="primary" style={{ fontSize: '14px' }}>
                                                {item.elo_rating}
                                            </Badge>
                                        </OverlayTrigger>
                                    </td>
                                    <td className="align-middle">
                                        <span className="text-muted">
                                            ±{Math.round((item.confidence_interval.upper - item.confidence_interval.lower) / 2)}
                                        </span>
                                    </td>
                                    <td className="align-middle">
                                        <Badge bg="secondary">{item.battles}</Badge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </Card.Body>
        </Card>
    );
};

export default EloLeaderboard;
